import type { Resolver } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@mint-lib/i18n';
import { z } from 'zod';

/** Values owned by the AdForm add-connection form (name + advertiser once an OAuth flow resolves). */
export interface AdFormAddFormValues {
  name: string;
  advertiser?: string;
}

/** Values owned by the AdForm manage-connection form (name is editable, advertiser is read-only). */
export interface AdFormManageFormValues {
  name: string;
  advertiserId: string | null;
  advertiserName: string | null;
}

export interface ConnectionNameConstraints {
  /** The connection's own current name, exempted from the uniqueness check. */
  connectionName?: string | null;
  existingConnectionNames?: string[];
}

function buildConnectionNameSchema(
  constraints: ConnectionNameConstraints,
  messages: { required: string; duplicate: string },
): z.ZodString {
  const normalizedConnectionName = (constraints.connectionName ?? '')
    .trim()
    .toLowerCase();
  const normalizedExistingConnectionNames = new Set(
    (constraints.existingConnectionNames ?? []).map((name) =>
      name.trim().toLowerCase(),
    ),
  );

  return z
    .string()
    .trim()
    .min(1, { message: messages.required })
    .refine((name) => {
      const normalizedName = name.trim().toLowerCase();

      if (normalizedName === normalizedConnectionName) {
        return true;
      }

      return !normalizedExistingConnectionNames.has(normalizedName);
    }, messages.duplicate);
}

export interface UseAdFormAddSchemaParams extends ConnectionNameConstraints {
  /** Once the AdForm OAuth callback resolves an `oauthFlowId`, the advertiser select becomes required. */
  oauthFlowId?: string | null;
}

/** Ported from the AdForm slice of `useTraffickingAddFormMethods` (legacy `packages/connections`). */
export function useAdFormAddSchema(
  params: UseAdFormAddSchemaParams,
): Resolver<AdFormAddFormValues> {
  const { t } = useTranslation('@mint-lib/adform-integration');

  const nameSchema = buildConnectionNameSchema(params, {
    required: t('This field is required.'),
    duplicate: t(
      'The Connection Name field must be unique. Please specify another name.',
    ),
  });

  return zodResolver(
    z.object({
      name: nameSchema,
      advertiser: params.oauthFlowId
        ? z.string().min(1, { message: t('Required') })
        : z.string().optional(),
    }),
  ) as Resolver<AdFormAddFormValues>;
}

/** Ported from the AdForm slice of `useTraffickingManageFormMethods` (legacy `packages/connections`). */
export function useAdFormManageSchema(
  params: ConnectionNameConstraints,
): Resolver<AdFormManageFormValues> {
  const { t } = useTranslation('@mint-lib/adform-integration');

  const nameSchema = buildConnectionNameSchema(params, {
    required: t('This field is required.'),
    duplicate: t(
      'The Connection Name field must be unique. Please specify another name.',
    ),
  });

  return zodResolver(
    z.object({
      name: nameSchema,
      advertiserId: z.string().nullable(),
      advertiserName: z.string().nullable(),
    }),
  ) as Resolver<AdFormManageFormValues>;
}
